import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, output, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { Subject, catchError, debounceTime, of, startWith, switchMap } from 'rxjs';
import { Translation } from '../../../../../core/i18n/translation.service';
import type { AssetDto } from '../../../../../core/models/assets/assets.dto';
import { AssetsService } from '../../../../../core/services/assets.service';
import { pickLocalizedTitle } from '../../assets/asset-titles';

/** A searchable asset picker: a search box that narrows a list of assets. `picked` carries the asset id ('' when cleared). */
@Component({
  selector: 'app-asset-select',
  standalone: true,
  imports: [TranslatePipe],
  templateUrl: './asset-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssetSelectComponent {
  private readonly assets = inject(AssetsService);
  private readonly translation = inject(Translation);
  private readonly destroyRef = inject(DestroyRef);
  private readonly terms = new Subject<string>();

  readonly allowNone = input(false);
  readonly picked = output<string>();
  protected readonly items = signal<AssetDto[]>([]);
  protected readonly current = signal('');

  constructor() {
    this.terms
      .pipe(
        startWith(''),
        debounceTime(250),
        switchMap((search) =>
          this.assets.getAssets({ skip: 0, rowCount: 50, search }).pipe(catchError(() => of({ items: [], totalCount: 0 }))),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((page) => {
        const items = page.items ?? [];
        this.items.set(items);
        // The option list was replaced: a pick that is no longer in it is cleared so the parent never keeps a stale id.
        if (this.current() && !items.some((a) => a.id === this.current())) this.pick('');
      });
  }

  protected label(asset: AssetDto): string {
    const isArabic = this.translation.getCurrentLang().toLowerCase().startsWith('ar');
    return `${asset.assetCode} - ${pickLocalizedTitle(asset.titleEn, asset.titleAr, isArabic)}`;
  }

  protected search(value: string): void {
    this.terms.next(value);
  }

  protected pick(value: string): void {
    this.current.set(value);
    this.picked.emit(value);
  }
}
